import { ColumnDef, CustomTable } from "../../../common/table";
import { pathNames, roleNames } from "../../../../constants/constants";
import { IUserAttendance } from ".";
import { useNavigate } from "react-router-dom";
import { AttendanceStatusEnum } from "../../../../types/common-types";
import PersonInfo from "../../../common/person-info";
import AttendanceStatusBadge from "../../../common/badge/AttendanceBadge";
import { formatMinutes } from "../../../../utils/date-format";
import TableStatusRow from "../../../common/table/TableStatusRow";
import CheckInCheckOut from "./CheckInCheckOut";

interface IUserAttendanceListProps {
  attendance: IUserAttendance[];
  refreshData: () => void;
}

export default function AttendanceTable({
  attendance,
  refreshData,
}: IUserAttendanceListProps) {
  const navigate = useNavigate();

  // Define configuration structures with isolated column custom components
  const columns: ColumnDef<IUserAttendance>[] = [
    {
      header: "#",
      className: "",
      render: (_, index) => index + 1,
    },
    {
      header: "Employee Name",
      className: "",
      render: (row) => (
        <PersonInfo
          personInfo={{
            profileImage: row.userId.profileImage,
            firstName: row.userId.firstName,
            lastName: row.userId.lastName,
            description: `${row.userId.userId} | ${roleNames[row.userId.role]}`,
          }}
          onClick={() =>
            navigate(`${pathNames.ATTENDANCE_DETAILS}/${row.userId._id}`)
          }
        />
      ),
    },
    {
      header: "Status",
      className: "text-center",
      render: (row) => {
        return (
          <>
            {row.attendanceStatus === AttendanceStatusEnum.WEEK_OFF ||
            row.attendanceStatus === AttendanceStatusEnum.HOLIDAY ? (
              {
                [AttendanceStatusEnum.WEEK_OFF]: (
                  <TableStatusRow title="Week Off" variant="warning" />
                ),
                [AttendanceStatusEnum.HOLIDAY]: (
                  <TableStatusRow title="Holiday" variant="info" />
                ),
              }[row.attendanceStatus]
            ) : (
              <div className="flex justify-center">
                <AttendanceStatusBadge status={row.attendanceStatus} />
              </div>
            )}
          </>
        );
      },
      colSpan: (row) =>
        row.attendanceStatus === AttendanceStatusEnum.WEEK_OFF ||
        row.attendanceStatus === AttendanceStatusEnum.HOLIDAY
          ? 4
          : 0,
    },
    {
      header: "Check In / Out",
      className: "text-center",
      hidden: (row) =>
        row.attendanceStatus === AttendanceStatusEnum.WEEK_OFF ||
        row.attendanceStatus === AttendanceStatusEnum.HOLIDAY
          ? true
          : false,
      render: (attendance) => (
        <CheckInCheckOut attendance={attendance} refreshData={refreshData} />
      ),
    },
    {
      header: "Late In / Early Out",
      className: "text-center",
      hidden: (row) =>
        row.attendanceStatus === AttendanceStatusEnum.WEEK_OFF ||
        row.attendanceStatus === AttendanceStatusEnum.HOLIDAY
          ? true
          : false,
      render: (row) => (
        <>
          {row.lateMinutes || row.earlyExitMinutes ? (
            <div className="grid grid-cols-2 gap-1 text-sm">
              <div className="flex items-center gap-1">
                <span className="text-gray-500">In</span>
                <span
                  className={
                    (row.lateMinutes && row.isLate)
                      ? "font-medium text-danger"
                      : "font-medium text-secondary"
                  }
                >
                  {row.lateMinutes ? formatMinutes(row.lateMinutes) : "-"}
                </span>
              </div>

              <div className="flex items-center gap-1">
                <span className="text-gray-500">Out</span>
                <span
                  className={
                    (row.earlyExitMinutes && row.isLate)
                      ? "font-medium text-danger"
                      : "font-medium text-secondary"
                  }
                >
                  {row.earlyExitMinutes
                    ? formatMinutes(row.earlyExitMinutes)
                    : "-"}
                </span>
              </div>
            </div>
          ) : (
            "-"
          )}
        </>
      ),
    },
    {
      header: "Total Hours",
      className: "text-center",
      hidden: (row) =>
        row.attendanceStatus === AttendanceStatusEnum.WEEK_OFF ||
        row.attendanceStatus === AttendanceStatusEnum.HOLIDAY
          ? true
          : false,
      render: (row) => (
        <div
          className={`flex justify-center font-medium ${row.isLate || row.earlyExitMinutes ? "text-danger" : "text-success"}`}
        >
          {row.totalWorkedMinutes ? formatMinutes(row.totalWorkedMinutes) : "-"}
        </div>
      ),
    },
  ];

  return (
    <>
      <CustomTable columns={columns} data={attendance} isBorder/>
    </>
  );
}
